import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Image as ImageIcon, Type, Sparkles, HeartHandshake, Swords } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useI18n } from '../context/I18nContext';

const ITEMS = [
  { key: 'aura_moment', icon: Sparkles, route: '/create/aura-moment', color: '#00F0FF', testid: 'create-aura-moment' },
  { key: 'photo',       icon: ImageIcon,route: '/create/photo',       color: '#FFE800', testid: 'create-photo' },
  { key: 'text',        icon: Type,     route: '/create/text',        color: '#00FFA3', testid: 'create-text' },
  { key: 'positive',    icon: HeartHandshake, route: '/create/positive-action', color: '#FF2E9F', testid: 'create-positive' },
];

/** Bottom sheet that appears when + is tapped, letting user choose post kind. */
export default function CreateSheet({ open, onClose }) {
  const nav = useNavigate();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const createInstantBattle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/events/quick', {});
      toast.success(`Batalla #EVT-${String(data.event_number).padStart(4, '0')} creada`);
      onClose();
      nav(`/events/${data.event_id}?quick=1`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la batalla');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="create-sheet">
      <div className="w-full aura-glass rounded-t-2xl border-t border-white/10 p-5 pb-[max(env(safe-area-inset-bottom),1rem)]" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/20" />
        <div className="font-display font-bold text-white text-lg mb-4 text-center">{t('feed.createTitle')}</div>

        {/* Big Create Battle CTA */}
        <button
          onClick={createInstantBattle}
          disabled={busy}
          data-testid="create-instant-battle"
          className="w-full mb-3 rounded-2xl p-4 relative overflow-hidden border border-aura-pink/40 bg-gradient-to-r from-aura-pink/15 via-aura-violet/15 to-aura-cyan/15 hover:from-aura-pink/25 hover:via-aura-violet/25 hover:to-aura-cyan/25 transition text-left"
        >
          <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-aura-pink/40 blur-3xl" />
          <div className="relative flex items-center gap-4">
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-aura-gradient text-black shadow-neon-cyan">
              <Swords size={22} strokeWidth={2.5} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] font-display font-black text-aura-pink">Instantáneo</div>
              <div className="font-display font-black text-white text-lg">{busy ? 'Creando…' : 'CREAR BATALLA'}</div>
              <div className="text-xs text-aura-textDim">El sistema asigna el número de evento y luego escaneas los QR de los contendientes.</div>
            </div>
          </div>
        </button>

        <div className="grid grid-cols-1 gap-2">
          {ITEMS.map(({ key, icon: Icon, route, color, testid }) => (
            <button
              key={key}
              onClick={() => { onClose(); nav(route); }}
              data-testid={testid}
              className="flex items-center gap-4 px-4 py-3 rounded-xl border border-white/10 bg-white/5 hover:border-aura-cyan/40 transition text-left"
            >
              <span className="grid place-items-center h-10 w-10 rounded-full" style={{ background: `${color}22`, boxShadow: `0 0 16px ${color}66` }}>
                <Icon size={20} style={{ color }} />
              </span>
              <div className="flex-1">
                <div className="font-display font-bold text-white">{t(`feed.create.${key}`)}</div>
                <div className="text-xs text-aura-textDim">{t(`feed.create.${key}Desc`)}</div>
              </div>
            </button>
          ))}
        </div>
        <button onClick={onClose} className="mt-4 w-full aura-btn-ghost" data-testid="create-close">
          <X size={16} /> {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
